import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { User } from '../../../../../store/user/user.models';

type DialogData = {
  channelName: string;
  users: User[];
};

@Component({
  selector: 'app-channel-add-participant-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule],
  template: `
    <section class="member-dialog" aria-label="Add user to group chat">
      <header class="member-dialog__header" mat-dialog-title>
        <div class="member-dialog__headline">
          <h2>Add User</h2>
          <p>Add someone to {{ data.channelName | titlecase }}</p>
        </div>

        <button type="button" mat-icon-button class="member-dialog__close" aria-label="Close add user dialog" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </header>

      <div mat-dialog-content class="member-dialog__content">
        <mat-form-field class="member-dialog__search" appearance="outline" subscriptSizing="dynamic">
          <mat-label>Search teammates</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            type="text"
            placeholder="Name or email"
            [value]="searchTerm()"
            (input)="onSearch(readValue($event))"
          />
        </mat-form-field>

        @if (filteredUsers().length > 0) {
          <div class="member-dialog__list" role="listbox" aria-label="Available users">
            @for (user of filteredUsers(); track user.id) {
              <button
                type="button"
                class="member-dialog__option"
                [class.member-dialog__option--selected]="selectedUserId() === user.id"
                [attr.aria-pressed]="selectedUserId() === user.id"
                (click)="selectUser(user.id)"
              >
                <span class="member-dialog__avatar">{{ getInitials(user.name) }}</span>
                <span class="member-dialog__identity">
                  <strong>{{ user.name | titlecase }}</strong>
                  <small>{{ user.email }}</small>
                </span>
                <span class="member-dialog__meta">
                  @if (selectedUserId() === user.id) {
                    <mat-icon class="member-dialog__check">check_circle</mat-icon>
                  } @else {
                    <span class="member-dialog__hint">Select</span>
                  }
                </span>
              </button>
            }
          </div>
        } @else {
          <div class="member-dialog__empty">
            <mat-icon>group_add</mat-icon>
            <h3>No users available</h3>
            <p>Everyone eligible for this group is already in the chat.</p>
          </div>
        }
      </div>

      <footer class="member-dialog__actions" mat-dialog-actions align="end">
        <button type="button" mat-stroked-button color="primary" (click)="close()">Cancel</button>
        <button type="button" mat-flat-button color="primary" [disabled]="!selectedUserId()" (click)="confirm()">Add User</button>
      </footer>
    </section>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    :host,
    :host * {
      box-sizing: border-box;
    }

    .member-dialog {
      width: 100%;
      max-width: none;
      padding: 10px 10px 8px;
      background: var(--linear-surface);
      border-radius: 14px;
      overflow: hidden;
    }

    .member-dialog__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 0 8px;
    }

    .member-dialog__headline {
      min-width: 0;
      flex: 1 1 auto;
      text-align: left;
    }

    .member-dialog__headline h2,
    .member-dialog__headline p,
    .member-dialog__empty h3,
    .member-dialog__empty p {
      margin: 0;
    }

    .member-dialog__headline h2 {
      color: var(--linear-ink);
      font-size: 1.2rem;
      line-height: 1.15;
      text-align: left;
    }

    .member-dialog__headline p {
      margin-top: 6px;
      color: var(--linear-muted);
      font-size: 0.9rem;
    }

    .member-dialog__close {
      width: 36px;
      height: 36px;
      min-width: 36px;
      min-height: 36px;
      flex: 0 0 auto;
      color: var(--mat-sys-on-surface-variant);
    }

    .member-dialog__close:hover {
      background: var(--mat-sys-surface-container-highest);
    }

    .member-dialog__content {
      padding: 0 10px 2px;
      margin: 0;
    }

    .member-dialog__search {
      width: 100%;
      margin-top: 14px;
    }

    .member-dialog__search .mat-icon {
      color: var(--linear-muted);
      font-size: 18px;
      width: 18px;
      height: 18px;
      margin-right: 8px;
    }

    .member-dialog__search input[type='text']::-webkit-search-decoration,
    .member-dialog__search input[type='text']::-webkit-search-cancel-button,
    .member-dialog__search input[type='text']::-webkit-search-results-button,
    .member-dialog__search input[type='text']::-webkit-search-results-decoration {
      display: none;
    }

    .member-dialog__list {
      display: grid;
      gap: 0.65rem;
      max-height: min(22rem, 50vh);
      overflow: auto;
      padding-right: 2px;
    }

    .member-dialog__option {
      display: flex;
      align-items: center;
      gap: 0.85rem;
      width: 100%;
      min-width: 0;
      border: 1px solid var(--linear-border);
      border-radius: 14px;
      padding: 0.8rem 0.9rem;
      background: var(--linear-surface);
      text-align: left;
      color: var(--linear-ink);
      cursor: pointer;
      transition: border-color 120ms ease, background 120ms ease, transform 120ms ease, box-shadow 120ms ease;
    }

    .member-dialog__option:hover {
      border-color: color-mix(in srgb, var(--mat-sys-primary) 48%, var(--linear-border));
      background: color-mix(in srgb, var(--mat-sys-primary-container) 32%, var(--linear-surface));
      transform: translateY(-1px);
    }

    .member-dialog__option--selected {
      border-color: var(--mat-sys-primary);
      background: color-mix(in srgb, var(--mat-sys-primary-container) 54%, var(--linear-surface));
      box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--mat-sys-primary) 24%, transparent);
    }

    .member-dialog__avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 999px;
      flex: 0 0 auto;
      background: color-mix(in srgb, var(--mat-sys-primary-container) 82%, #fff);
      color: var(--mat-sys-primary);
      font-size: 0.82rem;
      font-weight: 700;
    }

    .member-dialog__identity {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-width: 0;
    }

    .member-dialog__identity strong,
    .member-dialog__identity small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .member-dialog__identity strong {
      font-size: 0.92rem;
      color: var(--linear-ink);
    }

    .member-dialog__identity small {
      margin-top: 2px;
      color: var(--linear-muted);
      font-size: 0.8rem;
    }

    .member-dialog__meta {
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      min-width: 3.5rem;
      flex: 0 0 auto;
    }

    .member-dialog__check {
      color: var(--mat-sys-primary);
    }

    .member-dialog__hint {
      color: var(--linear-muted);
      font-size: 0.74rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .member-dialog__empty {
      display: grid;
      justify-items: center;
      gap: 0.45rem;
      min-height: 15rem;
      align-content: center;
      padding: 1.5rem 1rem;
      text-align: center;
    }

    .member-dialog__empty mat-icon {
      width: 2rem;
      height: 2rem;
      font-size: 2rem;
      color: var(--linear-muted);
    }

    .member-dialog__empty h3 {
      color: var(--linear-ink);
      font-size: 1rem;
    }

    .member-dialog__empty p {
      color: var(--linear-muted);
      font-size: 0.88rem;
      max-width: 22rem;
    }

    .member-dialog__actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 10px;
      padding: 4px 10px 0;
    }

    @media (max-width: 640px) {
      .member-dialog {
        padding: 8px 8px 6px;
        border-radius: 12px;
      }

      .member-dialog__header,
      .member-dialog__content,
      .member-dialog__actions {
        padding-left: 6px;
        padding-right: 6px;
      }

      .member-dialog__option {
        padding: 0.78rem 0.82rem;
      }

      .member-dialog__meta {
        min-width: 2.25rem;
      }

      .member-dialog__hint {
        display: none;
      }
    }
  `],
})
export class ChannelAddParticipantDialogComponent {
  protected readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ChannelAddParticipantDialogComponent, User | null>);
  protected readonly searchTerm = signal('');
  protected readonly selectedUserId = signal<string | null>(null);

  protected readonly filteredUsers = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.data.users;
    }

    return this.data.users.filter((user) =>
      user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)
    );
  });

  protected readValue(event: Event): string {
    return (event.target as HTMLInputElement | null)?.value ?? '';
  }

  protected onSearch(value: string): void {
    this.searchTerm.set(value);
  }

  protected selectUser(userId: string): void {
    this.selectedUserId.set(userId);
  }

  protected getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected close(): void {
    this.dialogRef.close(null);
  }

  protected confirm(): void {
    const selectedUser = this.data.users.find((user) => user.id === this.selectedUserId()) ?? null;
    this.dialogRef.close(selectedUser);
  }
}